// Build and print Notis Tunggakan letters, one selected row per page
// Usage: await printNotisPreview(rows)

import { apiSearchAkaun, apiGetAkaunByKodHarta, apiGetAkaunByNoAkaun } from "api/akaun";
import { apiGetInboisByNoInbois, apiGetInboisByNoAkaun } from "api/inbois";

const STAGE_TITLE = {
  pertama: "NOTIS TUNGGAKAN PERTAMA",
  kedua: "NOTIS TUNGGAKAN KEDUA",
  ketiga: "NOTIS TUNGGAKAN KETIGA",
};

function formatMsDate(d) {
  try {
    return new Date(d).toLocaleDateString("ms-MY");
  } catch (_) {
    return new Date().toLocaleDateString("ms-MY");
  }
}

// Format as strict DD/MM/YYYY (zero-padded)
function formatDDMMYYYY(d) {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch (_) {
    return "-";
  }
}

function formatRM(val) {
  const num = typeof val === "number" ? val : parseFloat(val);
  if (Number.isFinite(num)) {
    return num.toLocaleString("en-MY", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return null;
}

function buildAddress(row) {
  const parts = [
    row.alamat1_harta,
    row.alamat2_harta,
    row.poskod_harta,
    row.bandar_harta,
    row.negeri_harta,
  ]
    .map((p) => (typeof p === "string" ? p.trim() : ""))
    .filter(Boolean);
  return parts.join(", ");
}

function buildPage(row, idx, total) {
  const stage = STAGE_TITLE[row.stage] || "NOTIS";
  const today = new Date().toLocaleDateString("ms-MY");
  // Islamic (Hijri) date for Tarikh display
  const todayHijri = (() => {
    try {
      return new Intl.DateTimeFormat("ms-MY-u-ca-islamic", { day: "numeric", month: "long", year: "numeric" }).format(new Date());
    } catch (_) {
      return today;
    }
  })();
  const tarikhInbois = row.tarikh_inbois || "-";
  const nama = (row.nama || "-").toUpperCase();
  const alamatPenuh = buildAddress(row);
  const noInbois = row.no_inbois || "-";
  const bilHariLewat = row.bil_hari_lewat_bayar ?? "-";
  const perkara = row.perkara || "-";
  const jumlahRM = formatRM(row.jumlah);
  // Total tunggakan to mirror Lampiran 1 last-row total
  const _listUnpaid = Array.isArray(row._akaunInboisList) ? row._akaunInboisList : [];
  const jumlahTunggakan = _listUnpaid.reduce((s, r) => {
    const n = typeof r?.jumlah === "number" ? r.jumlah : parseFloat(r?.jumlah);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0) || (Number.isFinite(parseFloat(row?.jumlah)) ? parseFloat(row.jumlah) : 0);
  // Derive month groups (handle gaps) and per-month breakdown
  const fmtMY = (d) => d.toLocaleDateString("ms-MY", { month: "long", year: "numeric" }).toUpperCase();
  // Build map of YYYY-MM -> total amount for that month (in case there are multiple items per month)
  const monthTotals = new Map();
  for (const it of _listUnpaid) {
    const d = new Date(it?.tarikh_inbois);
    if (!d || Number.isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,'0')}`;
    const amt = typeof it?.jumlah === 'number' ? it.jumlah : parseFloat(it?.jumlah);
    const prev = monthTotals.get(key) || 0;
    monthTotals.set(key, prev + (Number.isFinite(amt) ? amt : 0));
  }
  const monthKeys = Array.from(monthTotals.keys()).sort();
  const bilBulan = monthKeys.length;
  // Group consecutive months into segments
  const toIndex = (key) => {
    const [y,m] = key.split('-').map((x) => parseInt(x,10));
    return y * 12 + (m - 1);
  };
  const fromKeyToLabel = (key) => {
    const [y,m] = key.split('-').map((x) => parseInt(x,10));
    const d = new Date(y, m-1, 1);
    return fmtMY(d);
  };
  const segments = [];
  let segStart = null, prevKey = null;
  for (const key of monthKeys) {
    if (segStart == null) { segStart = key; prevKey = key; continue; }
    if (toIndex(key) === toIndex(prevKey) + 1) {
      prevKey = key; // still consecutive
    } else {
      segments.push([segStart, prevKey]);
      segStart = key; prevKey = key;
    }
  }
  if (segStart != null) segments.push([segStart, prevKey]);
  // Build description: "MAC 2025 hingga JULAI 2025 dan DISEMBER 2025"
  const segmentTexts = segments.map(([a,b]) => {
    const A = fromKeyToLabel(a), B = fromKeyToLabel(b);
    return a === b ? `${A}` : `${A} hingga ${B}`;
  });
  let bulanDesc = "-";
  if (segmentTexts.length === 1) bulanDesc = segmentTexts[0];
  else if (segmentTexts.length > 1) bulanDesc = segmentTexts.slice(0,-1).join(", ") + " dan " + segmentTexts.slice(-1)[0];
  // Per-month breakdown only if all month totals are equal
  let perMonthText = "";
  if (bilBulan > 0) {
    const uniques = new Set(Array.from(monthTotals.values()).map((v) => Number.isFinite(v) ? v.toFixed(2) : "NaN"));
    if (uniques.size === 1) {
      const amt = parseFloat(Array.from(uniques)[0]);
      if (Number.isFinite(amt)) perMonthText = `(RM ${formatRM(amt)} sebulan x ${bilBulan} bulan)`;
    }
  }

  // Determine the last day of the month for the latest invoice date of this no_akaun,
  // prioritizing invoices that match the current notis stage (jenis notis)
  const berakhirTarikh = (() => {
    try {
      const list = Array.isArray(row._akaunInboisList) ? row._akaunInboisList : [];
      let latest = null;
      if (list.length) {
        const stageFlagKey = row.stage === "pertama" ? "notis_pertama" : row.stage === "kedua" ? "notis_kedua" : "notis_ketiga";
        const listByStage = list.filter((it) => String(it?.[stageFlagKey] ?? '0') === '1');
        const candidates = listByStage.length ? listByStage : list;
        for (const it of candidates) {
          const d = new Date(it?.tarikh_inbois);
          if (!Number.isNaN(d.getTime())) {
            if (!latest || d > latest) latest = d;
          }
        }
      } else if (row.tarikh_inbois) {
        const d = new Date(row.tarikh_inbois);
        if (!Number.isNaN(d.getTime())) latest = d;
      }
      if (!latest) return today;
      const endOfMonth = new Date(latest.getFullYear(), latest.getMonth() + 1, 0);
      return endOfMonth.toLocaleDateString("ms-MY");
    } catch (_) {
      return today;
    }
  })();
  // (Removed duplicate declarations of _listUnpaid, jumlahTunggakan, fmtMY, monthTotals, monthKeys, bilBulan)
  return `
    <section class="page">
      <header class="letterhead">
        <div class="left">
          <img src="/build/kejora.png" alt="KEJORA" class="logo" />
          <div class="agency">
            <div class="name">LEMBAGA KEMAJUAN JOHOR TENGGARA</div>
            <div class="name2">KEMENTERIAN KEMAJUAN DESA DAN WILAYAH</div>
            <div class="addr">Bangunan Ibu Pejabat KEJORA</div>
            <div class="addr">Jalan Dato' Hj. Hassan Yunus</div>
            <div class="addr">81930 Bandar Penawar</div>
            <div class="negeri">JOHOR DARUL TA'ZIM</div>
          </div>
        </div>
        <div class="contact">
          <div class="kc">Telefon</div><div class="vc">07-884-3000</div>
          <div class="kc">Hotline</div><div class="vc">1 800 888 620</div>
          <div class="kc">Faks</div><div class="vc">07-822 1600</div>
          <div class="kc">Emel</div><div class="vc">kejora@kejora.gov.my</div>
          <div class="kc">Laman Web</div><div class="vc">www.kejora.gov.my</div>
        </div>
      </header>
      <div class="header-divider"></div>

      <main>
        <div class="meta">
          <div class="recipient">
            <br/><div class="name">${nama}</div>
            <div class="name">${row.nama_syarikat || "-"}</div>
            <div class="name">${row.no_ssm || "-"}</div>
            <div class="addr">${row.alamat1_harta}</div>
            <div class="addr">${row.alamat2_harta}</div>
            <div class="addr">${row.poskod_harta} ${row.bandar_harta}</div>
            <div class="addr">${row.negeri_harta}</div>
          </div>
          <div class="ref-table">
            <div class="k">Rujukan Tuan</div><div class="v"> </div>
            <div class="k">Rujukan kami</div><div class="v">LKJT 600-8/3/191-STAIL</div>
            <div class="k"></div><div>&ensp;(&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;)</div>
            <div class="k">Tarikh</div><div class="v">${today}</div>
            <div class="k"></div><div class="v">${todayHijri}</div>
          </div>
        </div>
        <div class="body">
          <br/><div class="salutation">Tuan/Puan,</div>
        </div>
        <div class="subject">
          <span class="bold">TUNTUTAN BAYARAN TUNGGAKAN SEWAAN PREMIS</span>
          <br/>
          <span class="bold">PREMIS : ${alamatPenuh}.</span>
        </div>

        <div class="body">
          <p>Dengan hormatnya perkara di atas dirujuk.</p>
          <div class="items">
            <div class="item">
              2.&ensp;&ensp;&ensp;&ensp;Dukacita dimaklumkan, pihak tuan/puan didapati telah ingkar / gagal / enggan / cuai untuk menjelaskan bayaran sewaan bulanan premis tersebut di atas yang disewa daripada KEJORA yang sehingga kini telah tertunggak selama <span class="bold">3 bulan</span>. Jumlah keseluruhan tunggakan adalah <span class="bold">RM${formatRM(jumlahTunggakan) || "0.00"}</span> ${perMonthText ? `<span class="bold">${perMonthText}</span>` : ""} sebagaimana yang dinyatakan di <span class="bold">LAMPIRAN 1</span>.<br/>
            </div>
            <div class="item">
              3.&ensp;&ensp;&ensp;&ensp;Sehubungan dengan itu, pihak tuan/puan diminta untuk menjelaskan tunggakan tersebut yang berjumlah 
              <span class="bold">RM${formatRM(jumlahTunggakan) || "0.00"}</span> kepada KEJORA dalam tempoh <span class="bold">EMPAT BELAS (14) 
              hari</span> daripada tarikh notis ini.<br/>
            </div>
            <div class="item">
              4.&ensp;&ensp;&ensp;&ensp;<span class="bold-underline">SILA AMBIL PERHATIAN</span>, kegagalan pihak tuan/puan menjelaskan tunggakan tersebut dalam tempoh yang dinyatakan akan menyebabkan tindakan undang-undang selanjutnya diambil terhadap tuan/puan.<br/>
            </div>
            <div class="item">
              5.&ensp;&ensp;&ensp;&ensp;Kami sesungguhnya percaya langkah sedemikian tidak diperlukan dan berharap pihak tuan/puan dapat menjelaskan jumlah yang 
              dituntut dalam tempoh masa yang telah ditetapkan.<br/>
            </div>
            <div class="item">
              6.&ensp;&ensp;&ensp;&ensp;Sila abaikan notis ini sekiranya bayaran di atas telah dijelaskan sepenuhnya.<br/>
            </div>
            <p>Sekian, terima kasih.</p>
          </div>
        </div>
        <div class="signoff">
          <div  class="bold">"MALAYSIA MADANI"</div>
          <div class="bold">"BERKHIDMAT UNTUK NEGARA"</div>
          <div class="bold">"LUAR BANDAR SEJAHTERA"</div>
          <br/>
          <div>Saya yang menjalankan amanah,</div>
          <br/>
          <div class="bold">SH. KHAYULZAHRI BIN SH. A. RAOF</div>
          <br/>
          <div>Pengurus Pembangunan Ekonomi Dan Usahawan</div>
          <div>Bahagian Pembangunan Ekonomi Dan Usahawan</div>
        </div>
      </main>
      <div class="footer-divider">
        <div class="footnote">
          (Notis ini adalah cetakan komputer dan tidak memerlukan tandatangan)
        </div>
    </section>
  `;
}

function buildLampiranPage(row) {
  const list = Array.isArray(row._akaunInboisList) ? row._akaunInboisList : [];
  const nama = (row.nama || "-").toUpperCase();
  const noAkaun = row.no_akaun || row.noakaun || "-";
  const total = list.reduce((s, r) => {
    const n = typeof r.jumlah === "number" ? r.jumlah : parseFloat(r.jumlah);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);

  const rowsHtml = list
    .map((it, idx) => {
      const noInbois = it.no_inbois || "-";
  const tarikh = it.tarikh_inbois ? formatDDMMYYYY(it.tarikh_inbois) : "-";
      const perkara = it.perkara || "-";
      const jumlah = formatRM(it.jumlah) || "0.00";
      return `
        <tr>
        <td class="cell center">${idx + 1}</td>
        <td class="cell left">${noInbois}</td>
          <td class="cell center">${tarikh}</td>
          <td class="cell left">${perkara}</td>
          <td class="cell right">${jumlah}</td>
        </tr>`;
    })
    .join("");

  return `
    <section class="page">
      <header class="lampiran-head">
        <div class="lampiran-label">LAMPIRAN 1</div>
        <div class="lampiran-logo-block">
          <img src="/build/kejora.png" alt="KEJORA" class="lampiran-logo" />
          <div class="lampiran-agency-name">LEMBAGA KEMAJUAN JOHOR TENGGARA</div>
          <div class="lampiran-agency-short">(KEJORA)</div>
          <div class="lampiran-title">SENARAI TUNGGAKAN</div>
        </div>
      </header>
      <main class="lampiran-body">
        <div class="lampiran-meta">
          <div>Nama: <span class="value">${nama}</span></div>
          <div>No. Akaun (KEJORA): <span class="value">${noAkaun}</span></div>
        </div>
        <table class="lampiran-table" cellspacing="0" cellpadding="0">
          <thead>
            <tr>
              <th class="th center" style="width: 3%;">BIL</th>
              <th class="th center" style="width: 20%;">NO INBOIS</th>
              <th class="th center" style="width: 17%;">TARIKH INBOIS</th>
              <th class="th center" style="width: 50%;">PERKARA</th>
              <th class="th right" style="width: 10%;">JUMLAH</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
            <tr>
          <td class="cell right bold" colspan="4">JUMLAH</td>
              <td class="cell right bold">${formatRM(total) || "0.00"}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </main>
    </section>
  `;
}

function buildHtml(rows) {
  const pages = rows
    .map((r, i) => [buildPage(r, i, rows.length), buildLampiranPage(r)].join("\n"))
    .join("\n");
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Notis Tunggakan</title>
        <style>
          @page { size: A4; margin: 5mm 5mm 10mm; }
          body { font-family: Arial, sans-serif; color: #000; }
          .page { page-break-after: always; }
          .page:last-child { page-break-after: auto; }
          .page-break-before-element { page-break-before: always; }
          .letterhead { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; margin: 0 20px; }
          /* Scope header layout styles to the letterhead only */
          .letterhead .left { display: flex; gap: 12px; align-items: flex-end; flex: 1 1 auto; }
          .logo { width: auto; height: 110px; object-fit: contain; }
          .agency { font-size: 14px; line-height: 1.12; display: flex; flex-direction: column; justify-content: flex-start; gap: 2px; }
          .agency .addr { margin: 0; font-size: 14px; }
          .agency .name { font-weight: bold; font-size: 15px; letter-spacing: 0.5px; }
          .agency .name2 { font-weight: bold; font-size: 14px; letter-spacing: -0.5px; }
          .agency .negeri { font-weight: bold; font-size: 14px; letter-spacing: 0px; }
          .header-divider { border-top: 1px solid #000; margin: 15px 0 15px; }
          .footer-divider { width: 100%; border-top: 1px solid #000; bottom: 0; left: 0; margin-top: 55px; }
          .first-page-footer { bottom: 0px; display: flex; justify-content: flex-end; }
          .first-page-footer .brand { display: flex; flex-direction: row; justify-content: flex-end; align-items: flex-end; }
          .first-page-footer .iso-logo { display: flex; height: 60px; width: auto; object-fit: contain; justify-content: flex-end; margin-top: 5px; }
          .meta { display: flex; justify-content: space-between; align-items: flex-start; margin: 8px 40px 0; }
          .stage-label { font-weight: bold; color: #d32f2f ; margin-top: 20px}
          .ref-table { display: grid; grid-template-columns: max-content 1fr; column-gap: 6px; row-gap: 2px; justify-content: end; align-items: baseline; font-size: 15px; }
          .ref-table .k { text-align: left; }
          .ref-table .v { text-align: left; }
          .ref-table .v::before { content: ": "; }
          .ref-sign { display: grid; grid-template-columns: max-content 1fr; column-gap: 6px; row-gap: 2px; justify-content: end; align-items: baseline; margin-top: 8px; font-size: 15px; }
          .ref-sign .ks { text-align: left; }
          .ref-sign .vs { text-align: left; }
          .contact { display: grid; grid-template-columns: max-content 1fr; column-gap: 6px; row-gap: 1px; justify-items: start; align-items: end; margin-top: 8px; margin-left: auto; align-self: flex-end; }
          .contact .kc { font-size: 8px; text-align: left; }
          .contact .vc { font-size: 8px; text-align: left; }
          .contact .vc::before { content: ": "; }
          .recipient { font-size: 15px; max-width: 50%; }
          .recipient .name { font-weight: bold; font-size: 15px; }
          .recipient .addr { font-size: 15px; }
          .subject { margin: 14px 40px 0; font-size: 15px; }
          .subject .bold { font-weight: bold; text-transform: uppercase; }
          .body { font-size: 15px; margin: 0 40px; }
          /* Unnumbered item list formatting to mimic paragraph points */
          .body .items { display: flex; flex-direction: column; gap: 16px; line-height: 1; }
          .body .item { text-align: justify; }
          .sublist { margin-top: 1px; }
          .sublist { margin-top: 1px; list-style: decimal; }
          .bold { font-weight: bold; }
          .bold-underline { font-weight: bold; text-decoration: underline; }
          .salutation { margin-bottom: 20px; margin-top: 20px; font-size: 15px; }
          .signoff { margin: 10px 40px 0; font-size: 15px; }
          .footnote { font-size: 10px; text-align: center; color: #000000ff; margin-top: 5px; }
          .footnote-notis { font-size: 10px; text-align: center; color: #000000ff; margin-top: 20px; }

          /* Lampiran styles */
          .lampiran-head { position: relative; padding-top: 8mm; }
          .lampiran-label { font-weight: bold; position: absolute; top: 0; right: 0; font-size: 15px; }
          .lampiran-logo-block { display: flex; flex-direction: column; align-items: center; gap: 6px; margin-top: 4mm; }
          .lampiran-logo { width: auto; height: 80px; object-fit: contain; }
          .lampiran-agency-name { font-weight: bold; text-align: center; font-size: 15px; }
          .lampiran-agency-short { font-weight: bold; text-align: center; font-size: 15px; }
          .lampiran-title { margin-top: 8px; font-weight: bold; text-align: center; font-size: 15px; }
          .lampiran-body { margin: 10mm 40px 0; font-size: 15px;  }
          .lampiran-meta { margin-bottom: 6mm; font-size: 14px; }
          .lampiran-meta .value { font-weight: bold; font-size: 14px; }
          .lampiran-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          .lampiran-table th, .lampiran-table td { border: 1px solid #333; }
          .lampiran-table .th { border: 1px solid #333; padding: 6px; font-weight: bold; font-size: 13px; }
          .lampiran-table .cell { border: 1px solid #333; padding: 6px; font-size: 13px; }
          /* Generic text alignment helpers for table content */
          .left { text-align: left; }
          .center { text-align: center; }
          .right { text-align: right; }
        </style>
      </head>
      <body>
        ${pages}
      </body>
    </html>
  `;
}

export default async function printNotisPreview(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) {
    alert("Sila pilih sekurang-kurangnya satu rekod untuk dicetak.");
    return false;
  }

  // Enrich rows with full address from akaun (if not already present)
  const cache = new Map();
  const getAddrFromRecord = (rec) => ({
  nama_syarikat: rec?.nama_syarikat ?? rec?.customer?.nama_syarikat ?? rec?.syarikat ?? "",
  no_ssm: rec?.no_ssm ?? rec?.customer?.no_ssm ?? rec?.ssm ?? "",
    alamat1_harta: rec?.alamat1_harta ?? rec?.customer?.alamat1_harta ?? "",
    alamat2_harta: rec?.alamat2_harta ?? rec?.customer?.alamat2_harta ?? "",
    poskod_harta: rec?.poskod_harta ?? rec?.customer?.poskod_harta ?? "",
    bandar_harta: rec?.bandar_harta ?? rec?.customer?.bandar_harta ?? "",
    negeri_harta: rec?.negeri_harta ?? rec?.customer?.negeri_harta ?? "",
  });
  const fetchAkaunByNoAkaun = async (no_akaun) => {
    if (!no_akaun) return null;
    if (cache.has(`no_akaun:${no_akaun}`)) return cache.get(`no_akaun:${no_akaun}`);
    try {
      const res = await apiGetAkaunByNoAkaun(no_akaun);
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      const rec = arr[0] || null;
      const val = rec ? getAddrFromRecord(rec) : null;
      cache.set(`no_akaun:${no_akaun}`, val);
      return val;
    } catch { return null; }
  };
  const fetchAkaunByKodharta = async (kodharta) => {
    if (!kodharta) return null;
    if (cache.has(`kodharta:${kodharta}`)) return cache.get(`kodharta:${kodharta}`);
    try {
      const res = await apiGetAkaunByKodHarta(kodharta);
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      const rec = arr[0] || null;
      const val = rec ? getAddrFromRecord(rec) : null;
      cache.set(`kodharta:${kodharta}`, val);
      return val;
    } catch { return null; }
  };
  const fetchAkaunByNama = async (nama) => {
    if (!nama) return null;
    if (cache.has(`nama:${nama}`)) return cache.get(`nama:${nama}`);
    try {
      const res = await apiSearchAkaun({ nama }, 1, 1);
      const arr = Array.isArray(res?.data) ? res.data : [];
      const rec = arr[0] || null;
      const val = rec ? getAddrFromRecord(rec) : null;
      cache.set(`nama:${nama}`, val);
      return val;
    } catch { return null; }
  };

  const enriched = [];
  for (const r of list) {
    let addr = null;
    if (r.no_akaun) addr = await fetchAkaunByNoAkaun(r.no_akaun);
    if (!addr && r.kodharta) addr = await fetchAkaunByKodharta(r.kodharta);
    if (!addr) addr = await fetchAkaunByNama(r.nama);
    // Fetch perkara/jumlah from inbois if missing
    let extraInbois = {};
    let baseInboisData = null;
    try {
      const needBase =
        (!r.no_akaun && !r.noakaun) ||
        (!r.perkara || r.perkara === "-") ||
        (r.jumlah === undefined || r.jumlah === null) ||
        (!r.tarikh_inbois);
      if (needBase && r.no_inbois) {
        const resp = await apiGetInboisByNoInbois(r.no_inbois);
        baseInboisData = resp?.data || resp; // some api wrappers return {data}
      }
      if (baseInboisData) {
        if ((!r.perkara || r.perkara === "-") && baseInboisData.perkara)
          extraInbois.perkara = baseInboisData.perkara;
        if ((r.jumlah === undefined || r.jumlah === null) && baseInboisData.jumlah != null)
          extraInbois.jumlah = baseInboisData.jumlah;
        if (!r.tarikh_inbois && baseInboisData.tarikh_inbois)
          extraInbois.tarikh_inbois = baseInboisData.tarikh_inbois;
        if ((!r.no_akaun && !r.noakaun) && baseInboisData.no_akaun)
          extraInbois.no_akaun = baseInboisData.no_akaun;
      }
    } catch (_) { /* ignore */ }
    // Fetch all inbois for this no_akaun (unpaid and non-zero), sorted by date
    let akaunInboisList = [];
    try {
      const noAkaun = r.no_akaun || r.noakaun || extraInbois.no_akaun || baseInboisData?.no_akaun;
      if (noAkaun) {
        const resp2 = await apiGetInboisByNoAkaun(noAkaun);
        const arr = Array.isArray(resp2?.data) ? resp2.data : Array.isArray(resp2) ? resp2 : [];
        const stageFlagKey = r.stage === 'pertama' ? 'notis_pertama' : r.stage === 'kedua' ? 'notis_kedua' : 'notis_ketiga';
        akaunInboisList = arr
          .filter((x) => String(x?.mark_bayar ?? '0') === '0')
          .filter((x) => {
            const n = typeof x?.jumlah === 'number' ? x.jumlah : parseFloat(x?.jumlah);
            return Number.isFinite(n) && n !== 0;
          })
          // New: only include invoices that match the current notis stage (jenis notis)
          .filter((x) => !r.stage ? true : String(x?.[stageFlagKey] ?? '0') === '1')
          .sort((a, b) => new Date(a.tarikh_inbois) - new Date(b.tarikh_inbois));
      }
    } catch (_) { /* ignore */ }

    // Merge addr fields only if they're missing in r
    const merged = { 
      ...r, 
      ...(extraInbois || {}),
      _akaunInboisList: akaunInboisList
    };
    // Only use addr fields if not already present or empty
    if (addr) {
      if (!merged.nama_syarikat) merged.nama_syarikat = addr.nama_syarikat;
      if (!merged.no_ssm) merged.no_ssm = addr.no_ssm;
      if (!merged.alamat1_harta) merged.alamat1_harta = addr.alamat1_harta;
      if (!merged.alamat2_harta) merged.alamat2_harta = addr.alamat2_harta;
      if (!merged.poskod_harta) merged.poskod_harta = addr.poskod_harta;
      if (!merged.bandar_harta) merged.bandar_harta = addr.bandar_harta;
      if (!merged.negeri_harta) merged.negeri_harta = addr.negeri_harta;
    }
    enriched.push(merged);
  }

  const win = window.open("", "_blank");
  if (!win) {
    alert("Gagal membuka tetingkap cetakan.");
    return false;
  }

  const html = buildHtml(enriched);
  win.document.write(html);
  win.document.close();
  setTimeout(() => {
    try { win.focus(); win.print(); } catch (_) {}
  }, 600);
  return true;
}
